import PropTypes from 'prop-types'
import {
  Box,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  SvgIcon,
  Typography,
  useMediaQuery,
} from '@mui/material'
import React from 'react'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import { formatDateWithTimezone } from '@hapana/shared/web/utils/time-utils'
import { getSiteDateFormat } from '@hapana/shared/web/utils/date-format-utils'
import useSiteStore from '_stores/site.store'
import { useGetSiteById } from '_hooks/gqty/sites.hooks'

import UpRightIcon from '@hapana/shared/web/styles/assets/svgs/arrow-up-right.svg'
import Loading from '@hapana/shared/web/components/shared/loading/loading.component'
import Button from '@hapana/shared/web/components/shared/button/button.component'
import { BUTTON_VARIANTS } from '@hapana/shared/web/components/shared/button/button.constants'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import { useExportRequest } from '_pages/exports/exports.hooks'

import { styles } from './export-request-list.styles'

const ExportRequestList = ({
  activeRequests = [],
  requestHistory = [],
  idsLoading = [],
  isLoading = false,
  onRemoveRequest,
  onClearRequestHistory,
  onClose,
}) => {
  const { t } = useTranslation(['common', 'core'])
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const { closeDialog } = useDialog()
  const activeExportRequest = useExportRequest()
  const siteId = useSiteStore((state) => state.currentSite?.siteId)
  const { data: siteData } = useGetSiteById(siteId)
  const siteTimezone = siteData?.timezone

  return (
    <Box sx={styles.menuBox}>
      <Box sx={styles.activeRequestsBox}>
        {isLoading ? (
          <Loading />
        ) : activeRequests?.length > 0 ? (
          <Stack>
            <Box sx={styles.activeRequestTitle}>
              <Typography sx={styles.activeRequestsTypography} variant="header4">
                {activeRequests?.length} {t('exportWidgets.activeRequests')}
              </Typography>

              {!requestHistory.length && (
                <Button
                  variant={BUTTON_VARIANTS.TEXT}
                  label={t('close')}
                  onClick={onClose}
                  data-testid="export-request-list-close"
                />
              )}
            </Box>

            {activeRequests?.map((request) => (
              <MenuItem
                key={request.id}
                sx={styles.activeRequestItem}
                disabled={!!idsLoading && idsLoading.length}
              >
                <Box sx={styles.activeRequestWrapper}>
                  <Typography variant="header4" sx={styles.activeRequestItemLabel}>
                    {t(`exports.reports.${request.export}.title`)}
                  </Typography>
                  <Typography variant="small" sx={styles.activeRequestItemStatus}>
                    {t(`exports.reports.${request.status}`)}
                  </Typography>
                </Box>
                {idsLoading && idsLoading.includes(request.id) ? (
                  <Loading />
                ) : (
                  <IconButton
                    size="small"
                    onClick={() => onRemoveRequest(request.id)}
                    data-testid="export-request-list-close-icon"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </MenuItem>
            ))}
          </Stack>
        ) : null}
      </Box>

      <Box
        sx={activeRequests.length > 0 ? styles.requestHistoryBox : styles.requestHistoryBoxWhite}
      >
        <Box sx={styles.requestHistoryHeader}>
          <Typography variant="header4" sx={styles.requestHistoryTitle}>
            {t('exportWidgets.requestHistory')}
          </Typography>

          <Button
            variant={BUTTON_VARIANTS.TEXT}
            onClick={onClearRequestHistory}
            label={t('exportWidgets.clearHistory')}
            disabled={!requestHistory?.length > 0}
            data-testid="export-request-list-clear-history"
          />

          {!activeRequests.length && isMobile && (
            <Box sx={styles.closeButton}>
              <Divider orientation="vertical" flexItem sx={styles.divider} />

              <Button variant={BUTTON_VARIANTS.TEXT} label={t('close')} onClick={closeDialog} />
            </Box>
          )}
        </Box>
        {requestHistory?.length > 0 ? (
          requestHistory.map((history) => (
            <MenuItem key={history.id}>
              <Box sx={styles.requestHistoryItem}>
                <Typography variant="header4" sx={styles.requestHistoryItemLabel}>
                  {t(`exports.reports.${history.export}.title`)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {formatDateWithTimezone(
                    history.createdAt,
                    siteTimezone,
                    getSiteDateFormat('mediumWithTimeAt')
                  )}
                </Typography>
              </Box>

              <IconButton
                size="small"
                onClick={() => {
                  activeExportRequest.mutate(history.id)
                }}
                data-testid="export-request-list-up-right-icon"
              >
                <SvgIcon component={UpRightIcon} fontSize="large" sx={styles.upRightIconStyles} />
              </IconButton>
            </MenuItem>
          ))
        ) : (
          <Typography variant="body2" color="textSecondary" sx={styles.noHistoryTypography}>
            {t('exportWidgets.noRequestHistory')}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

ExportRequestList.propTypes = {
  activeRequests: PropTypes.array.isRequired,
  requestHistory: PropTypes.array.isRequired,
  exportWidgets: PropTypes.bool,
  idsLoading: PropTypes.array,
  isLoading: PropTypes.bool,
  onRemoveRequest: PropTypes.func.isRequired,
  onClearRequestHistory: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default ExportRequestList
